import { type ColumnDef, createColumnHelper } from "@tanstack/react-table"

export interface VideoRow {
  id: string
  title: string
  unit_number: number | null
  week_number: number | null
  part_number: number | null
  status: "processing" | "draft" | "published" | "archived"
  price_kes: number
  thumbnail_url: string | null
  edit_url: string
}

const col = createColumnHelper<VideoRow>()

const statusConfig = {
  processing: { badge: "bg-amber-500/10 text-amber-500 border-amber-500/20", dot: "bg-amber-500" },
  draft: { badge: "bg-secondary text-muted-foreground border-border", dot: "bg-muted-foreground" },
  published: { badge: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20", dot: "bg-emerald-500" },
  archived: { badge: "bg-secondary text-muted-foreground border-border", dot: "bg-border" },
} as const

type PushEvent = (event: string, payload: object, cb?: (reply: unknown) => void) => void

export function makeVideoColumns(pushEvent: PushEvent): ColumnDef<VideoRow, any>[] {
  return [
    col.accessor("title", {
      header: "Title",
      cell: (info) => {
        const row = info.row.original
        return (
          <div className="flex items-center gap-3 min-w-0">
            {row.thumbnail_url ? (
              <img src={row.thumbnail_url} alt="" className="size-9 rounded-md object-cover shrink-0 bg-secondary" />
            ) : (
              <div className="size-9 rounded-md bg-secondary border border-border flex items-center justify-center shrink-0">
                <span className="text-[0.6rem] text-muted-foreground">▶</span>
              </div>
            )}
            <p className="text-[0.8125rem] font-medium text-foreground truncate max-w-[18rem] leading-tight">
              {row.title}
            </p>
          </div>
        )
      },
    }),
    col.accessor("unit_number", {
      header: "Unit",
      cell: (info) => (
        <span className="text-xs text-foreground tabular-nums">
          {info.getValue() != null ? `Unit ${info.getValue()}` : "—"}
        </span>
      ),
    }),
    col.display({
      id: "week_part",
      header: "Wk / Pt",
      cell: (info) => {
        const row = info.row.original
        const parts = [
          row.week_number != null && `W${row.week_number}`,
          row.part_number != null && `P${row.part_number}`,
        ].filter(Boolean)
        return (
          <span className="text-xs text-muted-foreground tabular-nums">
            {parts.length > 0 ? parts.join(" · ") : "—"}
          </span>
        )
      },
    }),
    col.accessor("status", {
      header: "Status",
      cell: (info) => {
        const s = info.getValue() as keyof typeof statusConfig
        const cfg = statusConfig[s] ?? statusConfig.draft
        return (
          <span className={`inline-flex items-center gap-1.5 text-[0.7rem] font-medium px-2 py-0.5 rounded-md border capitalize ${cfg.badge}`}>
            <span className={`size-1.5 rounded-full ${cfg.dot}`} />
            {s}
          </span>
        )
      },
    }),
    col.accessor("price_kes", {
      header: "Price",
      cell: (info) => (
        <span className="text-xs font-medium text-foreground tabular-nums">KES {info.getValue()}</span>
      ),
    }),
    col.display({
      id: "actions",
      header: "Actions",
      size: 200,
      cell: (info) => {
        const row = info.row.original
        const nextStatus: Record<string, string | null> = {
          draft: "published",
          published: "archived",
          processing: "draft",
          archived: null,
        }
        const next = nextStatus[row.status]
        return (
          <div className="flex items-center gap-2">
            <a
              href={row.edit_url}
              className="inline-flex items-center h-7 px-2.5 rounded-md text-[0.7rem] font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            >
              Edit
            </a>
            {next && (
              <button
                onClick={() => pushEvent("set_status", { id: row.id, status: next })}
                className="inline-flex items-center h-7 px-2.5 rounded-md text-[0.7rem] font-medium text-primary hover:bg-primary/10 transition-colors capitalize"
              >
                → {next}
              </button>
            )}
            <button
              onClick={() => {
                if (confirm(`Delete "${row.title}"? This cannot be undone.`)) {
                  pushEvent("delete_video", { id: row.id })
                }
              }}
              className="inline-flex items-center h-7 px-2.5 rounded-md text-[0.7rem] font-medium text-destructive hover:bg-destructive/10 transition-colors"
            >
              Delete
            </button>
          </div>
        )
      },
    }),
  ]
}
